/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Sparkles,
  Calculator,
  MessageSquare,
  Ruler,
  BookOpen,
  BarChart3,
  Receipt,
  Layers,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "AI Built for Roofing Contractors",
  title: "AI for roofing contractors",
  titleHighlight: "for residential and commercial crews.",
  description: `One platform where AI helps roofing contractors bid faster, match costs to real supplier rates, audit scope before proposals ship, and run projects from one database. Built for 3–25 person teams. Grounded in your real business data — not generic construction averages.`,

  featureGrid: {
    title: "Where AI shows up for roofing contractors",
    items: [
      {
        icon: Sparkles,
        title: "Material database",
        description:
          "Shingles, TPO, EPDM, underlayment, fasteners — real supplier pricing.",
      },
      {
        icon: Sparkles,
        title: "Tear-off + install tracking",
        description:
          "Estimate tear-off labor separately from new install labor.",
      },
      {
        icon: Sparkles,
        title: "Pitch and complexity variance",
        description:
          "Steep pitch jobs take longer — variance factors adjust production rates.",
      },
      {
        icon: Sparkles,
        title: "Waste factors per material",
        description:
          "Automatic waste calculation based on pitch, complexity, and material type.",
      },
      {
        icon: Sparkles,
        title: "Residential + commercial",
        description: "Steep-slope and low-slope work in one system.",
      },
      {
        icon: Sparkles,
        title: "Hank for roofers",
        description:
          "'What did we charge per square for tear-off and re-roof last month?' Real answer.",
      },
    ],
  },

  workflow: {
    title: "How it works for roofing contractors",
    description:
      "From first bid to final invoice — every step in one database, every AI decision reviewable.",
    steps: [
      {
        label: "Measure",
        title: "Draw squares on the roof plan",
        description:
          "Upload the roof plan or import field measurements. Draw squares, ridge lines, and valleys on the drawing yourself — you're faster than any AI at reading a roof plan. AI picks up from there.",
        details: [],
      },
      {
        label: "Bid",
        title: "From squares to priced estimate",
        description:
          "AI matches every measured line to your cost database and applies the right waste factor based on material and complexity. Tear-off and new install tracked separately. Branded proposal in one click.",
        details: [],
      },
      {
        label: "Run",
        title: "Project tracking through completion",
        description:
          "Accepted bid becomes a project with material orders, crew assignments, and inspection tracking. Budget vs. actual on every job.",
        details: [],
      },
    ],
  },

  capabilities: [
    {
      icon: Sparkles,
      title: "Pitch-aware labor variance",
      description:
        "A 12:12 steep-slope roof takes dramatically longer than a 4:12 walkable roof. Variance factors adjust labor production rates based on pitch so your estimates don't lose money on steep jobs.",
      bullets: [
        "Pitch-based variance factors",
        "Walkable vs. fall-protection rates",
        "Production tracking from real jobs",
        "Historical comparison per pitch range",
      ],
    },
    {
      icon: Sparkles,
      title: "Waste factors that match reality",
      description:
        "Waste on a complex hip-and-valley roof is dramatically higher than a simple gable. Contractor Co-Pilot applies waste factors based on material type and roof complexity so your material orders are right the first time.",
      bullets: [
        "Waste factors per material and complexity",
        "Hip-and-valley vs. gable adjustment",
        "Starter, ridge, and hip counts separate",
        "Auto-applied during takeoff",
      ],
    },
    {
      icon: Sparkles,
      title: "Tear-off and install priced separately",
      description:
        "Tear-off labor and install labor are not the same number. Contractor Co-Pilot tracks them as separate line items so you can bid competitively on new construction and profitably on re-roofs.",
      bullets: [
        "Separate tear-off and install line items",
        "Dumpster and disposal tracked as costs",
        "Flashing and step-flashing counted separately",
        "Per-phase labor rates",
      ],
    },
    {
      icon: Sparkles,
      title: "Residential and commercial in one workspace",
      description:
        "Steep-slope residential and low-slope commercial are fundamentally different jobs, but most roofers do both. Contractor Co-Pilot keeps them in one system with different cost databases and labor rates per work type.",
      bullets: [
        "Unified residential and commercial workflows",
        "TPO and EPDM cost tracking",
        "Commercial warranty tracking",
        "Separate labor rates per work type",
      ],
    },
  ],

  relatedFeatures: [
    { label: "Roofing Solutions", href: "/solutions/roofing", icon: Ruler },
    {
      label: "Roofing Calculator",
      href: "/tools/roofing-calculator",
      icon: Calculator,
    },
    {
      label: "How to Estimate a Roofing Job",
      href: "/blog/how-to-estimate-roofing-jobs",
      icon: BookOpen,
    },
    { label: "CC vs PlanSwift", href: "/compare/planswift", icon: BarChart3 },
    {
      label: "Construction Estimating",
      href: "/features/estimates",
      icon: Receipt,
    },
    { label: "Blueprint Takeoffs", href: "/features/takeoffs", icon: Layers },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: MessageSquare,
    },
    { label: "All Features", href: "/features", icon: Sparkles },
  ],
};

export function AiForRoofingContractorsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
